/**
 * Der Zuordnungsvorschlag für einen Eingangsvorgang — `INBOX-001`, `07` §7.4.
 *
 * Der Abschnitt verlangt „vorgeschlagenen Kunden und Auftrag **mit
 * Begründung**". Die Begründung ist nicht die Höflichkeitsform des Vorschlags,
 * sondern seine Voraussetzung: Ohne sie ist er für die Bürokraft nicht
 * prüfbar, sondern nur glaubwürdig — und das ist der Unterschied zwischen
 * einem Vorschlag und einer Automatik.
 *
 * **Regeln, keine KI.** ADR-017 hält die KI vollständig hinter dem Piloten,
 * und das ist hier kein Verzicht: Was eine Zuordnung trägt, sind harte
 * Merkmale — eine Auftragsnummer im Betreff, eine bekannte Absenderdomäne,
 * eine Bestellnummer aus einem Materialposten. Ein Modell, das „vermutlich
 * Bäckerei Hufnagel" sagt, hat keine bessere Begründung, sondern eine
 * unprüfbare.
 *
 * **Warum die Sicherheit als Wort angezeigt wird.** `sicherheitWort` in der
 * Oberfläche macht aus 0.86 „wahrscheinlich". Eine Prozentzahl liest sich wie
 * eine Messung und lädt zum Durchwinken ein; die Zahl hier ist die Summe von
 * Regelgewichten und misst nichts.
 */

import { formatResourceRef } from "@hwc/contracts";
import type { ResourceRef } from "@hwc/contracts";

import type { Bestand, Eingangsvorgang, Zuordnungsvorschlag } from "./modell.js";

/** Ein Merkmal, das für eine Zuordnung spricht. */
interface Treffer {
  readonly kunde?: ResourceRef;
  readonly auftrag?: ResourceRef;
  readonly gewicht: number;
  readonly grund: string;
}

/**
 * Eine Auftragsnummer im Text.
 *
 * Vier Ziffern, weil die Nummern im Betrieb so aussehen — und mit
 * Wortgrenzen, weil sonst jede Jahreszahl und jeder Betrag mitgelesen wird.
 * `2481` in „Rechnung über 2481,00 EUR" ist keine Auftragsnummer, und genau
 * solche Treffer machen einen Vorschlag unbrauchbar.
 */
function auftragsnummern(text: string): readonly string[] {
  return [...text.matchAll(/(?<![\d,.])(\d{4})(?![\d,.])/g)].map((m) => m[1]!);
}

/**
 * Freistehende Zahlenfolgen, aus denen eine Bestellnummer werden kann.
 *
 * Vier bis sechs Ziffern, dieselben Wortgrenzen wie oben: Was hier
 * herausfällt, wird gleich gegen die tatsächlich vergebenen Bestellnummern
 * geprüft — geraten wird nichts.
 */
function zahlenfolgen(text: string): readonly string[] {
  return [...text.matchAll(/(?<![\d,.])(\d{4,6})(?![\d,.])/g)].map((m) => m[1]!);
}

/** Die Domäne einer Adresse, klein geschrieben. */
function domaene(adresse: string): string {
  const teil = adresse.split("@")[1] ?? "";
  return teil.toLowerCase().trim();
}

/**
 * Berechnet den Vorschlag zu einem Vorgang.
 *
 * `undefined`, wenn nichts trägt. **Das ist der wichtigste Rückgabewert**: Ein
 * Vorschlag, der bei jedem Vorgang irgendetwas nennt, ist nach zwei Wochen
 * Rauschen, das niemand mehr liest — und dann geht auch der gute Vorschlag
 * unter. Lieber keine Aussage als eine schwache.
 */
export function berechneVorschlag(
  bestand: Bestand,
  vorgang: Pick<Eingangsvorgang, "betreff" | "absender" | "vorschau">,
): Zuordnungsvorschlag | undefined {
  const text = `${vorgang.betreff} ${vorgang.vorschau ?? ""}`;
  const treffer: Treffer[] = [];

  /*
   * 1. Auftragsnummer im Text — das stärkste Merkmal.
   *
   * Wer eine Nummer nennt, meint sie. Sie steht in der Betreffzeile jeder
   * Antwort auf eine Auftragsbestätigung und ist die einzige Angabe, die
   * genau einen Auftrag bezeichnet.
   */
  for (const nummer of auftragsnummern(text)) {
    const auftrag = bestand.auftraege.find((a) => a.nummer === nummer);
    if (auftrag) {
      treffer.push({
        auftrag: auftrag.ref,
        kunde: auftrag.kunde,
        gewicht: 0.6,
        grund: `Auftragsnummer ${nummer} steht im Betreff.`,
      });
    }
  }

  /*
   * 2. Absenderdomäne in der Kundenakte.
   *
   * Trägt den Kunden, nicht den Auftrag: Ein Kunde mit fünf laufenden
   * Aufträgen schreibt aus derselben Domäne über jeden davon.
   */
  const dom = domaene(vorgang.absender);
  if (dom) {
    for (const kunde of bestand.kunden) {
      const passt = kunde.kontakte.some(
        (k) => k.email !== undefined && domaene(k.email) === dom,
      );
      if (passt) {
        treffer.push({
          kunde: kunde.ref,
          gewicht: 0.4,
          grund: `Absenderdomäne ist in der Kundenakte ${kunde.nummer} hinterlegt.`,
        });
      }
    }
  }

  /*
   * 3. Eine Artikelnummer aus einem Materialposten.
   *
   * Der Fall Lieferant: Die Rechnung nennt keinen Auftrag, aber ein Artikel
   * daraus wurde genau einmal verbaut. Schwächer gewichtet, weil derselbe
   * Artikel in einem zweiten Auftrag stecken kann — und dann tragen zwei
   * Treffer gegeneinander, was die Summe unter die Schwelle drückt.
   */
  for (const posten of bestand.material) {
    if (!posten.artikelnummer) continue;
    if (!text.toUpperCase().includes(posten.artikelnummer.toUpperCase())) continue;

    const einsatz = bestand.einsaetze.find(
      (e) => formatResourceRef(e.ref) === formatResourceRef(posten.einsatz),
    );
    const auftrag = einsatz
      ? bestand.auftraege.find(
          (a) => formatResourceRef(a.ref) === formatResourceRef(einsatz.auftrag),
        )
      : undefined;

    if (auftrag) {
      treffer.push({
        auftrag: auftrag.ref,
        kunde: auftrag.kunde,
        gewicht: 0.3,
        grund:
          `Artikel ${posten.artikelnummer} wurde in Auftrag ${auftrag.nummer} verbaut.`,
      });
    }
  }

  /*
   * 4. Bestellnummer aus einem Materialposten — der Lieferantenfall.
   *
   * **Die häufigste Rechnung im Korb ist die eines Großhändlers**, und sie
   * nennt keinen Auftrag: Der Lieferant kennt ihn nicht. Was sie nennt, ist
   * die Bestellnummer aus der Betreffzeile — und die gehört zu genau einer
   * Bestellung zu genau einem Auftrag.
   *
   * Deshalb wiegt sie fast so viel wie eine Auftragsnummer (0.55 zu 0.6) und
   * trägt allein über die Schwelle. Der kleine Abstand ist Absicht: Nennt eine
   * Mail beides und widersprechen sie sich, gewinnt die Auftragsnummer — die
   * hat ein Mensch getippt, der den Auftrag meinte.
   */
  const folgen = new Set(zahlenfolgen(text));
  if (folgen.size > 0) {
    const gesehen = new Set<string>();
    for (const posten of bestand.material) {
      if (!posten.bestellnummer || !folgen.has(posten.bestellnummer)) continue;

      const einsatz = bestand.einsaetze.find(
        (e) => formatResourceRef(e.ref) === formatResourceRef(posten.einsatz),
      );
      const auftrag = einsatz
        ? bestand.auftraege.find(
            (a) => formatResourceRef(a.ref) === formatResourceRef(einsatz.auftrag),
          )
        : undefined;
      if (!auftrag) continue;

      /*
       * Eine Bestellung umfasst mehrere Posten. Zählte jeder einzeln, wöge
       * eine Sammelbestellung schwerer als eine Auftragsnummer — und zwar
       * allein deshalb, weil viel auf einmal bestellt wurde.
       */
      const schluessel = `${posten.bestellnummer}|${formatResourceRef(auftrag.ref)}`;
      if (gesehen.has(schluessel)) continue;
      gesehen.add(schluessel);

      treffer.push({
        auftrag: auftrag.ref,
        kunde: auftrag.kunde,
        gewicht: 0.55,
        grund:
          `Bestellnummer ${posten.bestellnummer} gehört zum Material `
          + `von Auftrag ${auftrag.nummer}.`,
      });
    }
  }

  if (treffer.length === 0) return undefined;

  /*
   * Zusammenzählen je Auftrag. Der beste gewinnt — aber nur, wenn er die
   * Schwelle reiszt und nicht mit einem zweiten gleichauf liegt.
   */
  const nachAuftrag = new Map<string, { treffer: Treffer[]; summe: number }>();
  const ohneAuftrag: Treffer[] = [];

  for (const t of treffer) {
    if (!t.auftrag) {
      ohneAuftrag.push(t);
      continue;
    }
    const schluessel = formatResourceRef(t.auftrag);
    const bisher = nachAuftrag.get(schluessel) ?? { treffer: [], summe: 0 };
    nachAuftrag.set(schluessel, {
      treffer: [...bisher.treffer, t],
      summe: bisher.summe + t.gewicht,
    });
  }

  /**
   * **Ein Kundentreffer verstärkt den Auftrag desselben Kunden.**
   *
   * Der erste Entwurf legte ihn beiseite, weil er keinen Auftrag nennt — und
   * damit war „Auftrag 2481" von der Bäckerei genauso sicher wie dieselbe
   * Nummer von einer wildfremden Adresse. Gemessen am 05.09.2026: beide 0.6.
   *
   * Das ist offensichtlich falsch. Zwei unabhängige Merkmale, die auf dasselbe
   * zeigen, wiegen mehr als eines — und die Begründung wird dadurch besser,
   * nicht nur die Zahl: Sie nennt jetzt beides, und die Bürokraft sieht, dass
   * Nummer **und** Absender zusammenpassen.
   */
  for (const t of ohneAuftrag) {
    if (!t.kunde) continue;
    const kundenRef = formatResourceRef(t.kunde);
    for (const [schluessel, eintrag] of nachAuftrag) {
      const passt = eintrag.treffer.some(
        (e) => e.kunde !== undefined && formatResourceRef(e.kunde) === kundenRef,
      );
      if (!passt) continue;
      nachAuftrag.set(schluessel, {
        treffer: [...eintrag.treffer, t],
        summe: eintrag.summe + t.gewicht,
      });
    }
  }

  const sortiert = [...nachAuftrag.values()].sort((a, b) => b.summe - a.summe);
  const bester = sortiert[0];
  const zweiter = sortiert[1];

  /*
   * **Zwei gleich starke Treffer ergeben keinen Vorschlag.**
   *
   * Wenn derselbe Artikel in zwei Aufträgen steckt, ist die richtige Antwort
   * nicht „vermutlich der erste", sondern keine. `07` §7.4 lässt die
   * Bürokraft entscheiden; ihr eine Münze zu werfen und das Ergebnis
   * „Vorschlag" zu nennen, wäre schlechter als zu schweigen.
   */
  if (bester && zweiter && Math.abs(bester.summe - zweiter.summe) < 0.15) {
    return undefined;
  }

  if (bester && bester.summe >= 0.4) {
    const kunde = bester.treffer.find((t) => t.kunde)?.kunde;
    return {
      auftrag: bester.treffer[0]!.auftrag!,
      ...(kunde ? { kunde } : {}),
      begruendung: bester.treffer.map((t) => t.grund).join(" "),
      sicherheit: Math.min(0.95, bester.summe),
    };
  }

  /*
   * Kein Auftrag, aber ein eindeutiger Kunde: auch das ist ein Vorschlag —
   * und für eine Anfrage ohne Auftragsbezug der einzig mögliche.
   */
  const kundenTreffer = ohneAuftrag.filter((t) => t.kunde);
  if (kundenTreffer.length === 1) {
    const t = kundenTreffer[0]!;
    return {
      kunde: t.kunde!,
      begruendung: t.grund,
      sicherheit: Math.min(0.95, t.gewicht + 0.5),
    };
  }

  return undefined;
}
